import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import ScrollToTop from "./components/ScrollToTop";
import GAListener from "./components/GAListener";
import AdminLayout from "./components/AdminLayout";
import { captureFirstTouchAttribution } from "./lib/attribution";

// Public / marketing
import Landing from "./pages/Landing";
import HowItWorks from "./pages/HowItWorks";
import ForAttorneys from "./pages/ForAttorneys";
import About from "./pages/About";
import FAQ from "./pages/FAQ";
import Contact from "./pages/Contact";
import States from "./pages/States";
import StateDetail from "./pages/StateDetail";
import CityDetail from "./pages/CityDetail";
import ServiceDetail from "./pages/ServiceDetail";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import FundingCostCalculator from "./pages/FundingCostCalculator";
import SettlementDistributionCalculator from "./pages/SettlementDistributionCalculator";
import NotFound from "./pages/NotFound";

// Intake
import Apply from "./pages/Apply";
import ApplySuccess from "./pages/ApplySuccess";

// Admin/auth
import Login from "./pages/Login";
import AdminDashboard from "./pages/AdminDashboard";        // legacy Kanban, now at /admin/pipeline
import AdminAppDetail from "./pages/AdminAppDetail";
import AdminInvitations from "./pages/AdminInvitations";
import AdminBlogIndex from "./pages/AdminBlogIndex";
import AdminBlogEditor from "./pages/AdminBlogEditor";
import AdminBlogTopicsIndex from "./pages/AdminBlogTopicsIndex";
import AdminBlogTopicEditor from "./pages/AdminBlogTopicEditor";

// Operations OS (new)
import OperationsDashboard from "./pages/OperationsDashboard";
import LoansIndex from "./pages/LoansIndex";
import LoanDetail from "./pages/LoanDetail";
import AdminOnlineApplications from "./pages/AdminOnlineApplications";
import AdminSettlementDiagnostic from "./pages/AdminSettlementDiagnostic";
import AdminClientGrouping from "./pages/AdminClientGrouping";
import FinancePortfolio from "./pages/FinancePortfolio";
import AccidentDetail from "./pages/AccidentDetail";
import FinanceUnitEconomics from "./pages/finance/FinanceUnitEconomics";
import FinanceVintage from "./pages/finance/FinanceVintage";
import FinanceRelationships from "./pages/finance/FinanceRelationships";
import FinanceCapital from "./pages/finance/FinanceCapital";
import FinanceCapitalStructures from "./pages/finance/FinanceCapitalStructures";
import FinanceContribution from "./pages/finance/FinanceContribution";
import FinanceProForma from "./pages/finance/FinanceProForma";
import FinanceSensitivity from "./pages/finance/FinanceSensitivity";
import FinanceCurves from "./pages/finance/FinanceCurves";
import FinanceAssumptions from "./pages/finance/FinanceAssumptions";
import AddFunding from "./pages/AddFunding";
import ClientsIndex from "./pages/ClientsIndex";
import ClientProfile from "./pages/ClientProfile";
import AttorneysIndex from "./pages/AttorneysIndex";
import AttorneyProfile from "./pages/AttorneyProfile";
import AgingView from "./pages/AgingView";
import PayoffsIndex from "./pages/PayoffsIndex";
import PayoffDetail from "./pages/PayoffDetail";
import TasksIndex from "./pages/TasksIndex";
import AlertsView from "./pages/AlertsView";
import ContractsIndex from "./pages/ContractsIndex";
import AdminTemplateManager from "./pages/AdminTemplateManager";
import AdminTemplateDeploy from "./pages/AdminTemplateDeploy";
import AdminContentSync from "./pages/AdminContentSync";
import ContractDetail from "./pages/ContractDetail";
import FirmsIndex from "./pages/FirmsIndex";
import FirmProfile from "./pages/FirmProfile";
import RequestsInbox from "./pages/RequestsInbox";
import CommunicationsCenter from "./pages/CommunicationsCenter";
import Unsubscribe from "./pages/Unsubscribe";
import { DemoAttorneyPortal, DemoAdminDashboard } from "./pages/Demo";

// Attorney portal (public token + persistent login)
import AttorneyPortal from "./pages/AttorneyPortal";
import AttorneyLogin from "./pages/AttorneyLogin";
import AttorneyDashboard from "./pages/AttorneyDashboard";
import AttorneyLoanDetail from "./pages/AttorneyLoanDetail";
import AttorneyClientDetail from "./pages/AttorneyClientDetail";
import AttorneyDocumentVault from "./pages/AttorneyDocumentVault";
import AttorneyCaseDetail from "./pages/AttorneyCaseDetail";
import AttorneyPayoff from "./pages/AttorneyPayoff";
import AttorneyMagicVerify from "./pages/AttorneyMagicVerify";
import MagicLinkRedeem from "./pages/MagicLinkRedeem";
import ImpersonateBoot from "./pages/ImpersonateBoot";
import AuthCallback from "./pages/AuthCallback";

// Detects the OAuth fragment BEFORE any ProtectedRoute renders.
function AppRoutes() {
  const location = useLocation();
  if (location.hash && location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Public marketing */}
      <Route path="/" element={<Landing />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/for-attorneys" element={<ForAttorneys />} />
      <Route path="/about" element={<About />} />
      <Route path="/faq" element={<FAQ />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/states" element={<States />} />
      <Route path="/states/ohio" element={<StateDetail />} />
      <Route path="/states/ohio/:city" element={<CityDetail />} />
      <Route path="/services/:slug" element={<ServiceDetail />} />
      <Route path="/accidents/:slug" element={<AccidentDetail />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/blogs" element={<Navigate to="/blog" replace />} />

      <Route path="/tools/funding-cost-calculator" element={<FundingCostCalculator />} />
      <Route path="/tools/settlement-distribution-calculator" element={<SettlementDistributionCalculator />} />

      <Route path="/apply" element={<Apply />} />
      <Route path="/apply/success" element={<ApplySuccess />} />

      <Route path="/login" element={<Login />} />

      {/* Public demo (no auth) */}
      <Route path="/demo" element={<DemoAdminDashboard />} />
      <Route path="/demo/admin" element={<DemoAdminDashboard />} />
      <Route path="/demo/attorney" element={<DemoAttorneyPortal />} />

      {/* New operations admin (sidebar shell) */}
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<OperationsDashboard />} />
        <Route path="loans" element={<LoansIndex />} />
        <Route path="loans/new" element={<AddFunding />} />
        <Route path="loans/:id" element={<LoanDetail />} />
        <Route path="clients" element={<ClientsIndex />} />
        <Route path="clients/:name" element={<ClientProfile />} />
        <Route path="attorneys" element={<AttorneysIndex />} />
        <Route path="attorneys/:email" element={<AttorneyProfile />} />
        <Route path="aging" element={<AgingView />} />
        <Route path="payoffs" element={<PayoffsIndex />} />
        <Route path="payoffs/:id" element={<PayoffDetail />} />
        <Route path="data-quality/client-groups" element={<AdminClientGrouping />} />
        <Route path="tasks" element={<TasksIndex />} />
        <Route path="alerts" element={<AlertsView />} />
        <Route path="contracts" element={<ContractsIndex />} />
        <Route path="templates" element={<AdminTemplateManager />} />
        <Route path="template-deploy" element={<AdminTemplateDeploy />} />
        <Route path="content-sync" element={<AdminContentSync />} />
        <Route path="contracts/:id" element={<ContractDetail />} />
        <Route path="firms" element={<FirmsIndex />} />
        <Route path="firms/:id" element={<FirmProfile />} />
        <Route path="requests" element={<RequestsInbox />} />
        <Route path="pipeline" element={<AdminDashboard />} />
        <Route path="invitations" element={<AdminInvitations />} />
        <Route path="blog" element={<AdminBlogIndex />} />
        <Route path="blog/topics" element={<AdminBlogTopicsIndex />} />
        <Route path="blog/topics/:id" element={<AdminBlogTopicEditor />} />
        <Route path="blog/new" element={<AdminBlogEditor />} />
        <Route path="blog/:slug" element={<AdminBlogEditor />} />
        <Route path="applications/:id" element={<AdminAppDetail />} />
        <Route path="online-applications" element={<AdminOnlineApplications />} />
        <Route path="finance/settlement-diagnostic" element={<AdminSettlementDiagnostic />} />
        <Route path="finance/portfolio" element={<FinancePortfolio />} />
        <Route path="finance/unit-economics" element={<FinanceUnitEconomics />} />
        <Route path="finance/vintage" element={<FinanceVintage />} />
        <Route path="finance/relationships" element={<FinanceRelationships />} />
        <Route path="finance/capital" element={<FinanceCapital />} />
        <Route path="finance/capital-structures" element={<FinanceCapitalStructures />} />
        <Route path="finance/contribution" element={<FinanceContribution />} />
        <Route path="finance/pro-forma" element={<FinanceProForma />} />
        <Route path="finance/sensitivity" element={<FinanceSensitivity />} />
        <Route path="finance/curves" element={<FinanceCurves />} />
        <Route path="finance/assumptions" element={<FinanceAssumptions />} />
        <Route path="communications" element={<CommunicationsCenter />} />
      </Route>

      {/* Public unsubscribe */}
      <Route path="/unsubscribe" element={<Unsubscribe />} />

      {/* Attorney portal */}
      <Route path="/attorney/:token" element={<AttorneyPortal />} />
      <Route path="/attorney-login" element={<AttorneyLogin />} />
      <Route path="/attorney/magic-verify" element={<AttorneyMagicVerify />} />
      <Route path="/portal/r/:token" element={<MagicLinkRedeem />} />
      <Route path="/portal/impersonate" element={<ImpersonateBoot />} />
      <Route path="/attorney/portal" element={<AttorneyDashboard />} />
      <Route path="/attorney/portal/documents" element={<AttorneyDocumentVault />} />
      <Route path="/attorney/portal/loans/:loan_id" element={<AttorneyLoanDetail />} />
      <Route path="/attorney/portal/clients/:client_key" element={<AttorneyClientDetail />} />
      <Route path="/attorney/portal/cases/:id" element={<AttorneyCaseDetail />} />
      <Route path="/attorney/portal/payoff/:id" element={<AttorneyPayoff />} />
      {/* Legacy URLs with GSC impressions — behaviour unchanged pending owner decision */}
      <Route path="/our-mission" element={null} />
      <Route path="/plaintiffs" element={null} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  // First-touch attribution: captured once per browser on app boot.
  // Persists in localStorage so multi-page journeys (blog → /apply) retain
  // the original landing page + referrer + utm parameters.
  useEffect(() => {
    captureFirstTouchAttribution();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <GAListener />
        <AppRoutes />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
